import { ChevronDown, ChevronsLeft, PanelLeft, Search, Settings2, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { ROTULO_PAPEL } from '@social/shared';
import { PainelAparencia } from '../componentes/painel-aparencia';
import { useAparencia } from '../lib/aparencia';
import { useSessao } from '../lib/sessao';
import { iniciaisDe } from './menu-usuario';
import { filtrarMenu, usePodeVer } from './menu';
import { Topbar } from './topbar';

/**
 * Casca do produto: barra lateral, barra do topo e a página atual em
 * `<Outlet />`.
 *
 * Portado do Adonai. Duas diferenças de fundo, e não de forma — a FORMA
 * (`filtrarMenu(filtro, podeVer)`, trilho, oculta, grupos fechados) é a
 * mesma. Uma terceira, de acessibilidade, foi corrigida nesta portagem: ver
 * `LARGURA_LG` abaixo.
 *
 *  - `pode(permissao)` + `sessao.usuario.telas` (matriz de permissões e
 *    acesso por tela) viram `usePodeVer()`, hoje implementado só com o papel
 *    — não temos os dois primeiros conceitos, e ninguém conseguiria testar um
 *    `pode` que sempre devolve `true`. A costura fica pronta para a F1d: só
 *    `usePodeVer` muda, `Shell` não sabe de onde vem a visibilidade.
 *  - `sessao.usuario.nome` vira `useSessao().eu?.nome`, carregado à parte por
 *    `GET /api/eu` (o token só carrega o papel). Enquanto `eu` não chega — ou
 *    se a chamada falhar — cai para `ROTULO_PAPEL[papel]`.
 */
/**
 * Breakpoint `lg` do Tailwind (mesmo valor de `lg:translate-x-0` na
 * `<aside>` abaixo) — é a partir daqui que a lateral deixa de deslizar para
 * fora da tela e passa a ficar sempre visível.
 */
const LARGURA_LG = 1024;

export function Shell() {
  const [menuAberto, setMenuAberto] = useState(false);
  const [aparenciaAberta, setAparenciaAberta] = useState(false);
  const [paletaAberta, setPaletaAberta] = useState(false);
  const [filtro, setFiltro] = useState('');
  const papel = useSessao((s) => s.papel);
  const eu = useSessao((s) => s.eu);
  const podeVer = usePodeVer();
  const trilho = useAparencia((a) => a.trilho);
  const oculta = useAparencia((a) => a.oculta);
  const alternarTrilho = useAparencia((a) => a.alternarTrilho);
  const gruposFechados = useAparencia((a) => a.gruposFechados);
  const alternarGrupo = useAparencia((a) => a.alternarGrupo);
  const { pathname } = useLocation();

  // O `<main>` e o scroller E o elemento da rota de LAYOUT: o no sobrevive a
  // troca de rota. O navegador so zera (ou restaura, no voltar) a rolagem do
  // DOCUMENTO — um `<div>` scroller guarda o `scrollTop` da pagina anterior,
  // e o detalhe da sociedade abriria no meio em vez de no titulo.
  const refConteudo = useRef<HTMLElement>(null);
  useEffect(() => {
    // `scrollTop = 0` e nao `scrollTo(0, 0)`: `Element.scrollTo` nao existe
    // no jsdom, e o teste da casca quebrava antes de exercitar qualquer
    // comportamento. A propriedade faz a mesma coisa e existe em todo lugar.
    if (refConteudo.current) refConteudo.current.scrollTop = 0;
  }, [pathname]);

  // Abaixo de `lg` a gaveta fechada sai da TELA (`-translate-x-full`), mas
  // continua no DOM e na ordem de tabulação: sem isto, tabular a partir da
  // topbar entra numa barra invisível (busca, cada item, aparência, trilho) e
  // o foco some por sete ou oito paradas. `inert` tira a barra fechada da
  // tabulação sem mexer no layout nem na animação.
  //
  // Em `lg`+ a `<aside>` fica sempre visível por CSS (`lg:translate-x-0`),
  // então ela nunca pode ficar inerte ali — senão o menu fixo do desktop
  // perderia o teclado. Por isso o estado de largura é rastreado aqui: não há
  // como condicionar um atributo de HTML a um breakpoint só em CSS.
  const [abaixoDeLg, setAbaixoDeLg] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < LARGURA_LG,
  );
  useEffect(() => {
    const aoRedimensionar = () => setAbaixoDeLg(window.innerWidth < LARGURA_LG);
    window.addEventListener('resize', aoRedimensionar);
    return () => window.removeEventListener('resize', aoRedimensionar);
  }, []);

  const asideFechada = abaixoDeLg && !menuAberto;
  const asideRef = useRef<HTMLElement>(null);
  useEffect(() => {
    // `toggleAttribute`, não a propriedade `.inert` — suporte mais consistente
    // entre navegadores (e é o que faz o atributo aparecer de verdade no DOM).
    asideRef.current?.toggleAttribute('inert', asideFechada);
  }, [asideFechada]);

  // No trilho não cabe caixa de busca; manter o filtro ativo ali esconderia
  // itens sem o usuário ver por quê.
  const filtroAtivo = trilho ? '' : filtro.trim();
  const grupos = useMemo(() => filtrarMenu(filtroAtivo, podeVer), [filtroAtivo, podeVer]);

  const rotuloPapel = papel ? (ROTULO_PAPEL[papel] ?? papel) : '';

  // `eu` chega numa segunda chamada e pode nunca chegar (ver `buscarEu` em
  // `lib/sessao.ts`) — por isso o nome só aparece quando existe de verdade.
  // Sem este cuidado, o rodapé piscaria um nome vazio no instante entre o
  // login e a resposta de `/api/eu`.
  const nomeExibido = eu?.nome ?? rotuloPapel;
  const subtitulo = eu ? `${rotuloPapel} · ${eu.escritorio.nome}` : '';
  const identidadeTrilho = eu
    ? `${eu.nome} — ${rotuloPapel} · ${eu.escritorio.nome}`
    : rotuloPapel;

  return (
    <div className="flex h-dvh overflow-hidden">
      {menuAberto && (
        <div
          className="bg-palco/40 fixed inset-0 z-40 lg:hidden"
          onClick={() => setMenuAberto(false)}
          aria-hidden
        />
      )}

      <aside
        ref={asideRef}
        // Sem `sticky`: a página não rola mais (o `<main>` rola), então
        // `sticky` seria inerte. A lateral é filha de um flex de `h-screen`
        // e herda a altura dele — o rodapé (quem está logado) fica sempre à
        // vista, e quem rola é o `<nav>` de dentro, que já tem
        // `flex-1 overflow-y-auto`.
        className={`border-tinta-100 bg-superficie fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r transition-[transform,width] duration-200 lg:static lg:translate-x-0 ${
          trilho ? 'lg:w-[4.5rem]' : 'lg:w-64'
        } ${menuAberto ? 'translate-x-0' : '-translate-x-full'} ${
          // Escondida no desktop: sai do fluxo em vez de virar largura zero,
          // senão a borda direita continua desenhando uma linha órfã.
          oculta ? 'lg:hidden' : ''
        }`}
      >
        <div
          className={`flex items-center gap-2 px-4 py-4 ${trilho ? 'lg:justify-center lg:px-2' : ''}`}
        >
          {/* Decorativo: o texto ao lado já diz o nome do produto. */}
          <img
            src={`${import.meta.env.BASE_URL}marca-specializer.png`}
            alt=""
            aria-hidden="true"
            width={206}
            height={86}
            className="h-9 w-auto shrink-0"
          />
          {!trilho && (
            <p className="text-tinta-900 min-w-0 flex-1 truncate text-sm font-semibold">
              Specializer <span className="text-marca-600">Social</span>
            </p>
          )}
          <button
            className="text-tinta-500 hover:text-tinta-900 ml-auto lg:hidden"
            onClick={() => setMenuAberto(false)}
            aria-label="Fechar menu"
          >
            <X className="size-5" />
          </button>
        </div>

        {/*
          Busca do menu. Existe porque a lista cresce a cada fase, e procurar
          com o olho numa lista de itens é mais lento que digitar 3 letras.
        */}
        {!trilho && (
          <div className="relative px-3 pb-2">
            <Search className="text-tinta-500 pointer-events-none absolute top-2 left-6 size-3.5" />
            <input
              type="search"
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              placeholder="Buscar tela…"
              aria-label="Buscar tela no menu"
              className="border-tinta-100 bg-superficie-2 text-tinta-900 placeholder:text-tinta-500 focus:border-marca-500 w-full rounded-lg border py-1.5 pr-2 pl-8 text-xs transition-colors"
            />
          </div>
        )}

        <nav className="rolagem-fina flex-1 space-y-4 overflow-y-auto px-3 pb-2">
          {grupos.map((grupo) => {
            /*
              Três coisas mandam mais que a preferência de quem recolheu:

              1. O trilho não tem título, então não tem o que recolher.
              2. Buscando, tudo abre — esconder o que a busca ACHOU seria
                 responder "nada encontrado" para algo que está ali.
              3. O grupo da tela ABERTA fica aberto. Recolher o grupo em que se
                 está tiraria da vista a própria página atual, e a pessoa
                 perderia a referência de onde está no sistema.
            */
            const temAtiva = grupo.itens.some(
              (i) =>
                i.para === pathname ||
                (i.para !== '/' && pathname.startsWith(`${i.para}/`)),
            );
            const fechado =
              !trilho &&
              !filtroAtivo &&
              !temAtiva &&
              gruposFechados.includes(grupo.titulo);

            return (
              <div key={grupo.titulo}>
                {!trilho && (
                  <button
                    type="button"
                    onClick={() => alternarGrupo(grupo.titulo)}
                    aria-expanded={!fechado}
                    className="text-tinta-500 hover:text-tinta-900 flex w-full items-center gap-1 rounded px-3 pb-1 text-xs font-semibold tracking-wide uppercase transition-colors"
                  >
                    <ChevronDown
                      className={`size-3 shrink-0 transition-transform ${fechado ? '-rotate-90' : ''}`}
                      aria-hidden
                    />
                    <span className="truncate">{grupo.titulo}</span>
                    {fechado && (
                      <span className="text-tinta-300 ml-auto tabular-nums">
                        {grupo.itens.length}
                      </span>
                    )}
                  </button>
                )}
                <div className={`space-y-0.5 ${fechado ? 'hidden' : ''}`}>
                  {grupo.itens.map(({ para, rotulo, icone: Icone }) => (
                    <NavLink
                      key={para}
                      to={para}
                      end={para === '/'}
                      title={trilho ? rotulo : undefined}
                      onClick={() => setMenuAberto(false)}
                      className={({ isActive }) =>
                        `group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                          trilho ? 'lg:justify-center lg:px-0' : ''
                        } ${
                          isActive
                            ? 'bg-marca-100 text-marca-600 font-medium'
                            : 'text-tinta-700 hover:bg-marca-600/10 hover:text-tinta-900'
                        }`
                      }
                    >
                      {({ isActive }) => (
                        <>
                          {/* Marcador na borda: no trilho, o fundo sozinho não
                            diferencia o item ativo à distância. */}
                          {isActive && (
                            <span className="bg-marca-600 absolute inset-y-1.5 left-0 w-0.5 rounded-r" />
                          )}
                          {Icone && (
                            <Icone className="size-4 shrink-0 transition-transform group-hover:scale-110" />
                          )}
                          {!trilho && <span className="truncate">{rotulo}</span>}
                        </>
                      )}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}

          {grupos.length === 0 && (
            <p className="text-tinta-500 px-3 py-6 text-center text-xs">
              Nenhuma tela com “{filtroAtivo}”.
            </p>
          )}
        </nav>

        {/*
          Rodapé do menu: quem está logado.

          Fica FORA do `<nav>` de propósito — é o `flex-1 overflow-y-auto` dele
          que faz a lista rolar sozinha e este bloco ficar colado embaixo, sem
          subir junto com o menu.

          O "Sair" NÃO se repete aqui: ele mora na barra do topo, porque a
          lateral pode ser escondida por inteiro e a saída não pode morar só
          dentro do que se esconde. Aqui fica a identidade, que é o que responde
          "estou logado como quem?" sem precisar abrir menu nenhum.
        */}
        <div
          className={`border-tinta-100 flex items-center gap-2.5 border-t px-3 py-3 ${
            trilho ? 'lg:justify-center lg:px-2' : ''
          }`}
        >
          <span
            role={trilho ? 'img' : undefined}
            title={trilho ? identidadeTrilho : undefined}
            aria-label={trilho ? identidadeTrilho : undefined}
            className="bg-marca-600 text-marca-contraste flex size-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold"
          >
            {iniciaisDe(nomeExibido)}
          </span>
          {!trilho && (
            <div className="min-w-0 flex-1">
              <p className="text-tinta-900 truncate text-sm font-semibold">
                {nomeExibido}
              </p>
              {subtitulo && (
                <p className="text-tinta-500 truncate text-xs">{subtitulo}</p>
              )}
            </div>
          )}
        </div>

        <div className="border-tinta-100 flex gap-1 border-t px-3 py-3">
          <button
            type="button"
            onClick={() => setAparenciaAberta(true)}
            title="Aparência"
            aria-label="Aparência"
            className="text-tinta-700 hover:bg-marca-600/10 hover:text-tinta-900 flex flex-1 items-center justify-center gap-2 rounded-lg px-2 py-2 text-sm transition-colors"
          >
            <Settings2 className="size-4" />
            {!trilho && 'Aparência'}
          </button>
          <button
            type="button"
            onClick={alternarTrilho}
            title={trilho ? 'Expandir menu' : 'Recolher menu'}
            aria-label={trilho ? 'Expandir menu' : 'Recolher menu'}
            className="text-tinta-500 hover:bg-marca-600/10 hover:text-tinta-900 hidden rounded-lg p-2 transition-colors lg:block"
          >
            {trilho ? (
              <PanelLeft className="size-4" />
            ) : (
              <ChevronsLeft className="size-4" />
            )}
          </button>
        </div>
      </aside>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <Topbar
          aoAbrirMenu={() => setMenuAberto(true)}
          aoAbrirAparencia={() => setAparenciaAberta(true)}
          paletaAberta={paletaAberta}
          aoAbrirPaleta={() => setPaletaAberta(true)}
          aoFecharPaleta={() => setPaletaAberta(false)}
        />

        <main
          ref={refConteudo}
          className="rolagem-fina min-w-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain"
        >
          <div className="animate-entrada mx-auto w-full max-w-[1600px] p-4 md:p-6">
            <Outlet />
          </div>
        </main>
      </div>

      <PainelAparencia
        aberto={aparenciaAberta}
        aoFechar={() => setAparenciaAberta(false)}
      />
    </div>
  );
}
